// src/api/session.js
// Dónde viven los tokens de la web y cómo se renuevan.
//
// Access token: JWT corto (JWT_ACCESS_TTL, 10 min en dev). Refresh token:
// opaco, rotable, con detección de reuso en el backend (tokenService.js).
//
// Los dos van en localStorage. La clave del access sigue siendo 'token'
// porque hay pantallas que la leen directo. Guardar el refresh acá lo expone
// a XSS: la migración a cookie httpOnly está anotada como BLOQUEANTE antes de
// producción (CHECKLIST-PREVIO-A-PLAY), y mientras tanto el backend lo emite
// con REFRESH_TOKEN_TTL_DAYS=7 en lugar de 30.

import axios from 'axios';

const CLAVE_ACCESS  = 'token';
const CLAVE_REFRESH = 'refresh_token';

export const getAccessToken  = () => localStorage.getItem(CLAVE_ACCESS);
export const getRefreshToken = () => localStorage.getItem(CLAVE_REFRESH);

export function setTokens(access, refresh) {
  if (access)  localStorage.setItem(CLAVE_ACCESS, access);
  if (refresh) localStorage.setItem(CLAVE_REFRESH, refresh);
  // Para lo que no pasa por axios (el SSE de NotificationsBell): reconectar
  // con el token nuevo después de un re-login.
  window.dispatchEvent(new Event('fixo:tokens'));
}

export function clearTokens() {
  localStorage.removeItem(CLAVE_ACCESS);
  localStorage.removeItem(CLAVE_REFRESH);
}

// La sesión terminó de verdad: refresh rechazado (revocado, vencido o
// reusado), access inválido, o no queda ningún token. Sólo esto cierra sesión;
// una caída de red o un 5xx durante el refresh NO.
export class SesionTerminada extends Error {
  constructor(motivo) {
    super(`Sesión terminada: ${motivo}`);
    this.motivo = motivo;
  }
}

// ── Fin de sesión ───────────────────────────────────────────────────────────
// AuthContext registra acá qué hacer (mostrar el modal de re-login). Vive en
// este módulo y no en api.js para que el SSE, que no pasa por axios, cierre
// la sesión por el mismo camino.
let onSessionEnded = null;
export function setOnSessionEnded(fn) { onSessionEnded = fn; }

export function terminarSesion(motivo) {
  clearTokens();
  if (onSessionEnded) onSessionEnded(motivo);
}

// Cliente sin interceptores para /auth/refresh y /auth/logout: un 401 del
// refresh no puede volver a disparar un refresh.
export const clienteCrudo = axios.create({ baseURL: process.env.REACT_APP_API_URL });

// ── Refresh: uno por pestaña y uno por navegador ────────────────────────────
//
// Por pestaña: N requests que vencen juntos comparten UNA promesa.
//
// Entre pestañas: el backend rota el refresh y trata como robo cualquier uso
// de uno ya rotado — revoca TODA la familia. Si dos pestañas refrescan con el
// mismo token, la segunda deja a las dos deslogueadas. Por eso el refresh
// corre bajo navigator.locks, y adentro del candado se vuelve a mirar
// localStorage: si el access cambió respecto del que usó el request que
// falló, otra pestaña ya refrescó y se usa ese, sin llamar a /auth/refresh.
// Sin navigator.locks (navegadores viejos) queda sólo la protección por
// pestaña.

let refrescoEnCurso = null;

export function refrescarSesion(tokenUsado) {
  if (!refrescoEnCurso) {
    const tarea = () => refrescar(tokenUsado);
    refrescoEnCurso = (navigator.locks?.request
      ? navigator.locks.request('fixo-refresh', tarea)
      : tarea()
    ).finally(() => { refrescoEnCurso = null; });
  }
  return refrescoEnCurso;
}

async function refrescar(tokenUsado) {
  const accessActual = getAccessToken();
  if (accessActual && tokenUsado && accessActual !== tokenUsado) {
    return accessActual; // otra pestaña (o un refresh anterior) ya lo renovó
  }

  const refresh = getRefreshToken();
  if (!refresh) throw new SesionTerminada('sin_refresh');

  try {
    const { data } = await clienteCrudo.post('/auth/refresh', { refresh_token: refresh });
    const access = data.access_token || data.token;
    setTokens(access, data.refresh_token);
    return access;
  } catch (err) {
    if (err.response?.status === 401) throw new SesionTerminada('refresh_rechazado');
    throw err; // red o 5xx: la sesión puede seguir viva, no se cierra
  }
}

// Segundos que le quedan al access según su `exp`. Sólo lee el payload, no
// verifica la firma: es para decidir cuándo renovar, no para confiar en él.
function segundosRestantes(token) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return payload.exp - Date.now() / 1000;
  } catch {
    return 0;
  }
}

// Access vigente para lo que no pasa por axios (EventSource no manda headers
// y no ve el status del 401). Renueva si le quedan menos de 30 s.
// Devuelve null si la sesión terminó (y la cierra); lanza si el refresh falló
// por red.
export async function obtenerTokenVigente() {
  const token = getAccessToken();
  if (!token) return null;
  if (segundosRestantes(token) > 30) return token;
  try {
    return await refrescarSesion(token);
  } catch (e) {
    if (e instanceof SesionTerminada) {
      terminarSesion(e.motivo);
      return null;
    }
    throw e;
  }
}
